<template>
  <ErrorModal />
  <AddCustomer v-if="showAdd === true" :showAdd="showAdd" />
  <DxButton
    class="main-btn m-2"
    text="Toggle filters box"
    styling-mode="contained"
    @click="updateFiltersBoxVisibility(!showFiltersBox)"
    style="margin-left: 36px; margin-top: 24px"
  >
  </DxButton>
  <div class="customers-grid-filters-box" v-if="showFiltersBox">
    <DxTextBox
      v-model:value="additionalFilter.name"
      label="Name"
      color="black"
    />
    <DxTagBox
      :items="productFilters.dataSource"
      v-model:value="selectedProducts"
      display-expr="name"
      value-expr="value"
      :show-selection-controls="true"
      :select-all-text="'Select all'"
      :placeholder="'Product(s)'"
      apply-value-mode="instantly"
      tag-template="tag"
      style="margin-top: 12px"
    >
      <template #tag="{ data }">
        <div class="dx-tag-content">{{ data.name }}</div>
      </template>
    </DxTagBox>
    <DxTagBox
      :items="languageCodes"
      v-model:value="selectedLanguageCodes"
      display-expr="description"
      value-expr="id"
      :show-selection-controls="true"
      :select-all-text="'Select all'"
      :placeholder="'Language code(s)'"
      apply-value-mode="instantly"
      tag-template="tag"
      style="margin-top: 12px"
    >
      <template #tag="{ data }">
        <div class="dx-tag-content">{{ data.description }}</div>
      </template>
    </DxTagBox>
    <DxSelectBox
      :items="activeStatusOptions"
      v-model:value="additionalFilter.showActive"
      display-expr="desc"
      value-expr="val"
      :show-selection-controls="true"
      :select-all-text="'Select all'"
      :placeholder="'Active status'"
      apply-value-mode="instantly"
      tag-template="tag"
      style="margin-top: 12px"
    >
      <template #tag="{ data }">
        <div class="dx-tag-content">{{ data.desc }}</div>
      </template>
    </DxSelectBox>
    <DxButton
      class="main-btn m-2 search-customers-grid-btn"
      text="Search"
      styling-mode="contained"
      @click="refreshGrid()"
    >
    </DxButton>
  </div>
  <div class="customers-grid-container">
    <!-- Customers grid -->
    <div class="dx-card-custom">
      <DxDataGrid
        id="gridContainer"
        :data-source="customers"
        :key-expr="'id'"
        :allow-column-reordering="true"
        :remote-operations="true"
        :show-row-lines="true"
        :show-borders="true"
        ref="dataGridRef"
        :allow-column-resizing="true"
        column-resizing-mode="widget"
      >
        <DxToolbar style="width: 200px">
          <DxItem location="after" template="gridHeadingButtonTemplate" />
          <DxItem
            name="columnChooserButton"
            locate-in-menu="auto"
            location="after"
          />
        </DxToolbar>

        <template #gridHeadingButtonTemplate>
          <DxButton
            id="addCustomerBtn"
            class="main-btn m-2"
            text="Add new customer"
            styling-mode="contained"
            icon="plus"
            iconPosition="right"
            @click="enableAdd()"
          >
          </DxButton>
        </template>
        <DxPaging :page-size="50" />
        <DxPager
          :visible="true"
          :display-mode="compact"
          :show-navigation-buttons="true"
          :show-info="true"
          :show-page-size-selector="true"
          :info-text="pageInfoText"
        />
        <DxColumnChooser
          enabled="true"
          mode="select"
          height="370"
          width="400"
          title="Choose columns"
        >
          <DxColumnChooserSelection
            allow-select-all="true"
            select-by-click="true"
          />
          <DxPosition
            my="right top"
            at="right bottom"
            of=".dx-datagrid-column-chooser-button"
          />
        </DxColumnChooser>
        <DxColumn
          data-field="name"
          caption="Name"
          sort-order="asc"
          :allow-header-filtering="false"
        />
        <DxColumn
          width="35%"
          data-field="environment"
          caption="Products"
          alignment="left"
          :calculate-cell-value="setProductBadges"
          cell-template="statusCellTemplate"
          :lookup="productFilters"
          filter-type="include"
          :allow-filtering="false"
          :header-filter="headerFilterOptions"
          :calculate-filter-expression="customProductFilterExpression"
        />
        <template #statusCellTemplate="{ data }">
          <span
            v-for="badge in data.value"
            :key="badge.value"
            class="status-badge"
            :style="{
              backgroundColor: badge.bgColor,
              color: badge.color,
              padding: '6px',
              marginRight: '5px',
              borderRadius: '5px'
            }"
            :title="badge.hoverText"
          >
            {{ badge.name }}
          </span>
        </template>
        <DxColumn
          width="10%"
          data-field="languageCodeName"
          caption="Language"
          alignment="left"
          cell-template="languageCellTemplate"
          :allow-filtering="false"
          filter-type="include"
        />
        <template #languageCellTemplate="{ data }">
          <img
            v-if="data.data.languageCodeName === 'NL'"
            src="https://flagsapi.com/NL/flat/32.png"
            alt="Dutch"
          />
          <img
            v-else-if="data.data.languageCodeName === 'DE'"
            src="https://flagsapi.com/DE/flat/32.png"
            alt="German"
          />
        </template>
        <DxColumn
          data-field="startDate"
          caption="Start Date"
          :visible="false"
          :showInColumnChooser="false"
        />
        <DxColumn
          data-field="endDate"
          caption="End Date"
          :visible="false"
          :showInColumnChooser="false"
        />
        <DxColumn
          data-field="sasStartDate"
          caption="SAS Start Date"
          :visible="false"
          :showInColumnChooser="false"
        />
        <DxColumn
          data-field="sasEndDate"
          caption="SAS End Date"
          :visible="false"
          :showInColumnChooser="false"
        />
        <DxColumn
          width="10%"
          type="buttons"
          caption="Actions"
          :buttons="customerActionButtons"
        />
      </DxDataGrid>
    </div>

    <!-- Deactivate customer popup -->
    <DxPopup
      v-model:visible="deactivatePopupVisible"
      :drag-enabled="false"
      :show-title="false"
      :width="'auto'"
      :height="'auto'"
    >
      <template #content>
        <p class="row items-center">
          <i class="las la-2x la-ban la-fw"></i>
          Do you really want to deactivate this customer?
        </p>
        <div class="row justify-between">
          <DxButton
            class="orange-btn-filled popup-btn"
            text="Yes"
            @click="deactivateCustomer"
          ></DxButton>
          <DxButton
            class="orange-btn popup-btn"
            text="No"
            @click="closeDeactivatePopup"
          ></DxButton>
        </div>
      </template>
    </DxPopup>

    <!-- Remove customer popup -->
    <DxPopup
      v-model:visible="removePopupVisible"
      :drag-enabled="false"
      :show-title="false"
      :width="'auto'"
      :height="'auto'"
    >
      <template #content>
        <p class="row items-center">
          <i class="las la-2x la-trash-alt la-fw"></i>
          Do you really want to remove this customer?
        </p>
        <div class="row justify-between">
          <DxButton
            class="orange-btn-filled popup-btn"
            text="Yes"
            @click="removeCustomer"
          ></DxButton>
          <DxButton
            class="orange-btn popup-btn"
            text="No"
            @click="closeRemovePopup"
          ></DxButton>
        </div>
      </template>
    </DxPopup>
  </div>
</template>

<script setup>
import {
  DxDataGrid,
  DxColumn,
  DxColumnChooser,
  DxColumnChooserSelection,
  DxPosition,
  DxPaging,
  DxPager,
  DxToolbar,
  DxItem
} from "devextreme-vue/data-grid";
import { DxTextBox } from "devextreme-vue/text-box";
import { DxButton } from "devextreme-vue/button";
import { DxTagBox } from "devextreme-vue/tag-box";
import { DxSelectBox } from "devextreme-vue/select-box";

import { ref, onMounted, computed, onBeforeUnmount } from "vue";
import DxPopup from "devextreme-vue/popup";
import CustomersService from "../services/customers.service.js";
import AddCustomer from "../components/customers-components/AddCustomer.vue";
import ErrorModal from "../components/customers-components/ErrorModal.vue";
import createCustomStore from "../store/custom.store.js";
import moment from "moment";
import router from "../router/index.js";
import store from "../store/index.js";

const handleKeydown = e => {
  if (e.key === "Enter") {
    refreshGrid();
  }
};

onBeforeUnmount(() => {
  window.removeEventListener("keydown", handleKeydown);
});

onMounted(async () => {
  window.addEventListener("keydown", handleKeydown);
  await CustomersService.getLanguageCodes();
});

const selectedLanguageCodes = ref([]);
const selectedProducts = ref([]);

const languageCodes = computed({
  get: () => store.state.customers.languageCodes
});

const additionalFilter = ref({
  name: "",
  products: computed(() => selectedProducts.value.join(",")),
  languagecodes: computed(() => selectedLanguageCodes.value.join(",")),
  showActive: true
});

const showFiltersBox = ref(true);

const updateFiltersBoxVisibility = val => {
  showFiltersBox.value = val;
};

const customerActionButtons = [
  {
    hint: "Edit",
    icon: "las la-2x la-pencil-alt la-fw",
    onClick: e => goToEditCustomer(e.row.data.id)
  },
  {
    hint: "Deactivate",
    icon: "las la-2x la-ban la-fw",
    onClick: e => {
      customerId.value = e.row.data.id;
      deactivatePopupVisible.value = true;
    }
  },
  {
    hint: "Remove",
    icon: "las la-2x la-trash-alt la-fw",
    onClick: e => {
      customerId.value = e.row.data.id;
      removePopupVisible.value = true;
    }
  }
];
const productFilters = {
  dataSource: [
    { value: 0, name: "CNT (Content)" },
    { value: 10, name: "ISV" },
    { value: 20, name: "BSN (Business)" },
    { value: 30, name: "SKS" },
    { value: 40, name: "DRV" },
    { value: 50, name: "ISN (ISV no user)" },
    { value: 500, name: "SAS" }
  ],
  valueExpr: "value",
  displayExpr: "name"
};
const headerFilterOptions = {
  allowSelectAll: true,
  // Remove (Blanks) from filter options
  dataSource(options) {
    options.dataSource.postProcess = results => {
      return results.filter(item => item.value !== null && item.value !== "");
    };
  }
};

const customers = ref(
  createCustomStore("/api/customers", additionalFilter.value, "id")
);
const customerId = ref(null);
const dataGridRef = ref(null);
const deactivatePopupVisible = ref(false);
const removePopupVisible = ref(false);
const showAdd = computed(() => store.state.customers.showAdd);
const pageInfoText = computed(() => `Page {0} of {1} ({2} items)`);

const deactivateCustomer = async () => {
  await CustomersService.deactivateCustomer(customerId.value);
  refreshGrid();
  closeDeactivatePopup();
};
const closeDeactivatePopup = () => {
  deactivatePopupVisible.value = false;
};
const removeCustomer = async () => {
  await CustomersService.deleteCustomer(customerId.value);
  refreshGrid();
  closeRemovePopup();
};
const closeRemovePopup = () => {
  removePopupVisible.value = false;
};
const getColorBasedOnDate = (startDate, endDate) => {
  if (startDate == null || moment(startDate) > moment()) return "red";
  if (endDate != null && moment(endDate) < moment()) return "red";
  else if (
    endDate != null &&
    moment(endDate) >= moment() &&
    moment(endDate) <= moment().add(2, "months")
  )
    return "lightgreen";
  else return "green";
};
const refreshGrid = () => {
  dataGridRef.value?.instance?.getDataSource().reload();
};
const goToEditCustomer = id => {
  router.push({ name: "EditCustomer", params: { id } });
};
const enableAdd = () => {
  store.commit("customers/enableAdd");
};

const activeStatusOptions = ref([
  { val: true, desc: "Active only" },
  { val: false, desc: "Inactive only" },
  { val: null, desc: "All" }
]);

const setProductBadges = rowData => {
  const productBadges = [
    {
      value: 0,
      name: "CNT",
      bgColor: "lightGray",
      color: "whitesmoke",
      hoverText: "Content"
    },
    {
      value: 500,
      name: "SAS",
      bgColor: "lightGray",
      color: "whitesmoke",
      hoverText: "SAS"
    },
    {
      value: 10,
      name: "ISV",
      bgColor: "lightGray",
      color: "whitesmoke",
      hoverText: "ISV"
    },
    {
      value: 20,
      name: "BSN",
      bgColor: "lightGray",
      color: "whitesmoke",
      hoverText: "Business"
    },
    {
      value: 30,
      name: "SKS",
      bgColor: "lightGray",
      color: "whitesmoke",
      hoverText: "SKS"
    },
    {
      value: 40,
      name: "DRV",
      bgColor: "lightGray",
      color: "whitesmoke",
      hoverText: "DRV"
    },
    {
      value: 50,
      name: "ISN",
      bgColor: "lightGray",
      color: "whitesmoke",
      hoverText: "ISV no user"
    }
  ];

  switch (rowData.environment) {
    case 0: // Default (CNT and SAS)
      updateProductBadge(0, productBadges, rowData.startDate, rowData.endDate);
      updateProductBadge(
        500,
        productBadges,
        rowData.sasStartDate,
        rowData.sasEndDate
      );
      break;
    default:
      // Other envs
      updateProductBadge(
        rowData.environment,
        productBadges,
        rowData.startDate,
        rowData.endDate
      );
      break;
  }

  return productBadges;
};
const updateProductBadge = (
  environmentValue,
  productBadges,
  startDate,
  endDate
) => {
  const productBadge = productBadges.find(b => b.value === environmentValue);
  if (productBadge) {
    productBadge.color = "white";
    productBadge.bgColor = getColorBasedOnDate(startDate, endDate);
  }

  return productBadges;
};
</script>
